<template>
  <div ref="swiperElm" class="swiper">
    <div class="swiper-wrapper">
      <slot :swiper-ref="swiper" />
    </div>
    <div class="controls">
      <slot name="pagination">
        <div
          v-if="shouldCreateSlot(pagination, 'el')"
          ref="paginationElm"
          class="swiper-pagination"
        />
      </slot>
      <slot name="navigation">
        <div
          v-if="shouldCreateSlot(navigation, ['prevEl', 'nextEl'])"
          class="navigation"
        >
          <div ref="navigationPrevElm" class="swiper-button-prev" />
          <div ref="navigationNextElm" class="swiper-button-next" />
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import 'swiper/css'
import Swiper from 'swiper'
import { Pagination, Autoplay, Keyboard, Navigation } from 'swiper/modules'
import type {
  AutoplayOptions,
  NavigationOptions,
  PaginationOptions,
  SwiperModule,
  SwiperOptions
} from 'swiper/types'

const props = withDefaults(
  defineProps<{
    effect?: 'slide' | 'fade' | 'cube' | 'coverflow' | 'flip' | 'creative'
    slidesPerView?: number | 'auto'
    spaceBetween?: number | string
    centeredSlides?: boolean
    centeredSlidesBounds?: SwiperOptions['centeredSlidesBounds']
    pagination?: PaginationOptions | boolean
    autoplay?: AutoplayOptions | boolean
    loop?: boolean
    loopedSlides?: number | null
    keyboard?: boolean
    navigation?: NavigationOptions | boolean
    initialSlide?: number
    breakpoints?: SwiperOptions['breakpoints']
    autoHeight?: SwiperOptions['autoHeight']
  }>(),
  {
    effect: undefined,
    slidesPerView: 'auto',
    spaceBetween: 0,
    pagination: false,
    autoplay: false,
    keyboard: false,
    navigation: undefined,
    initialSlide: undefined,
    loopedSlides: undefined,
    breakpoints: undefined,
    centeredSlidesBounds: undefined,
    autoHeight: undefined
  }
)

const emit = defineEmits<{
  (e: 'slideChange', value: Swiper): void
  (e: 'init', value: Swiper): void
}>()

const swiperElm = ref<HTMLDivElement>()
const paginationElm = ref<HTMLDivElement>()
const navigationPrevElm = ref<HTMLDivElement>()
const navigationNextElm = ref<HTMLDivElement>()

const swiperModules: SwiperModule[] = []
if (props.pagination) swiperModules.push(Pagination)
if (props.autoplay) swiperModules.push(Autoplay)
if (props.keyboard) swiperModules.push(Keyboard)
if (props.navigation) swiperModules.push(Navigation)

const swiper = ref<Swiper>()
onMounted(() => {
  if (!swiperElm.value) return
  swiper.value = new Swiper(swiperElm.value, {
    ...props,
    modules: swiperModules,
    pagination: shouldCreateSlot(props.pagination, 'el')
      ? { ...props.pagination, el: paginationElm.value }
      : props.pagination,
    navigation: shouldCreateSlot(props.navigation, ['prevEl', 'nextEl'])
      ? {
          ...props.navigation,
          prevEl: navigationPrevElm.value,
          nextEl: navigationNextElm.value
        }
      : props.navigation,
    on: {
      init(s: Swiper) {
        emit('init', s)
      },
      slideChange(s: Swiper) {
        emit('slideChange', s)
      }
    }
  })
})

onBeforeUnmount(() => {
  if (swiper.value && !swiper.value.destroyed) {
    swiper.value.destroy?.()
  }
})

defineExpose({ swiper })

const shouldCreateSlot = (
  prop: (typeof props)[keyof typeof props],
  properties: string | string[]
): prop is object => {
  if (!prop || typeof prop !== 'object') return false
  const propsToCheck = Array.isArray(properties) ? properties : [properties]
  return !propsToCheck.every(p => p in prop)
}

provide('swiper', swiper)
</script>

<style lang="scss" scoped>
.swiper {
  display: flex;
  flex-direction: column;
}
</style>
